import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGithub, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import {
  PortfolioContainer,
  HeroSection,
  HeroContent,
  Name,
  Title,
  SocialLinks,
  SocialLink,
  QuoteCard,
  QuoteText,
  SectionTitle,
  SearchContainer,
  SearchInput,
  RepositorySection,
  RepositoryGrid,
  RepositoryCard,
  RepoTitle,
  RepoLanguage,
  LeftContent,
  FeaturedProject,
  ProjectDots,
  Dot,
  ScrollButton,
  ScrollText,
  ViewProjectLink,
  OrganizationHeader,
  OrganizationName,
  RoleTitle,
  QuoteContent,
  CardFooter,
  ProjectDescription,
  Navigation,
  NavList,
  NavItem,
  NavLink,
  MobileNavButton,
  MobileNav,
  HeroBackground,
  BioText,
  AvatarContainer,
  InteractiveHint,
} from './Portfolio.styles';
import Contact from './Contact';
import AnimalCrossingAvatar from './AnimalCrossingAvatar';
import MinimalCaseStudy from './MinimalCaseStudy';
import CompanyLogos from './CompanyLogos';
import { microsoftArchitectures } from '../data/microsoftArchitecture';

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  const sections = useRef([
    { id: 'hero', label: 'Home' },
    { id: 'repositories', label: 'Work' },
    { id: 'contact', label: 'Contact' }
  ]);



  // Manual project entries to display at the top
  // Top 3 Projects - Minimal Style
  const featuredProjects = [
    {
      name: "Xbox Data Governance",
      role: "Data Engineering Intern · Microsoft · Jun – Sep 2026",
      tagline: "Two data governance tools for Xbox's Databricks estate: automated compliance checks and a central hub for job health, with an AI agent.",
      tags: ["Azure Databricks", "Python", "SQL", "React", "Databricks Genie"],
      visual: <CompanyLogos />,
      details: [
        "Problem: Engineers reviewed Databricks jobs against governance policy by hand, and inventory and job health were split across separate workspaces",
        "Solution: Built a compliance linter to automate those policy reviews, and an observability platform that brings inventory, job health, and failure history into one React dashboard and Genie AI agent",
        "Impact: Caught 51% of jobs missing required tags and identified thousands of hours of failed compute each day"
      ],
      metrics: [
        { value: 1000, suffix: "+", label: "Hours of failed compute identified daily", hasCount: true },
        { value: 51, suffix: "%", label: "Jobs missing required tags", hasCount: true },
        { value: 300, suffix: "+", label: "Jobs identified with no owner, retry, or alert", hasCount: true }
      ],
      architectures: microsoftArchitectures
    },
    {
      name: "CTD Data Validator",
      role: "Software Engineer · CCHDO / NOAA · Sep 2024 – Mar 2026",
      tagline: "Reduces processing time for researchers by 95%, presented at UN-sponsored conference",
      tags: ["TypeScript", "Vite", "fflate"],
      visualBg: "#F0F8FF",
      liveUrl: "https://cchdo.github.io/submission-validator/",
      previewUrl: "https://cchdo.github.io/submission-validator/",
      allowIframe: true,
      details: [
        "Problem: Researchers spent days manually validating CTD data with error-prone processes",
        "Solution: Built validation tool with 200+ rules and O(1) parameter lookup catching 99% of data quality issues",
        "Impact: Reduces processing time by 95%, presented at UN-sponsored OSM26 conference"
      ],
      metrics: [
        { value: 50000, suffix: "+", label: "Monthly Active Users", hasCount: true },
        { value: 200, suffix: "+", label: "Validation Rules", hasCount: true },
        { value: "UN", label: "Presented at endorsed conference" }
      ],
      showFlow: true,
      flowData: {
        nodes: [
          // Top Layer - Input & Parsing (y: 20)
          { id: 'upload', x: 10, y: 20, label: 'File / ZIP Input', sublabel: 'fflate unzip' },
          { id: 'detect', x: 28, y: 20, label: 'File-Type Detect', sublabel: 'CTD vs Bottle' },
          { id: 'structure', x: 46, y: 20, label: 'Structure Check', sublabel: 'Columns & headers' },
          { id: 'csv', x: 64, y: 20, label: 'CSV Parser', sublabel: 'Quote handling' },
          { id: 'extract', x: 82, y: 20, label: 'Build Records', sublabel: 'Rows → cells' },

          // Middle Layer - Validation Engine (y: 50) - reversed order
          { id: 'measure', x: 20, y: 50, label: 'Measurements', sublabel: 'Type / flag / range' },
          { id: 'formats', x: 40, y: 50, label: 'Format Checks', sublabel: 'EXPOCODE, dates' },
          { id: 'params', x: 60, y: 50, label: 'Parameter Rules', sublabel: '200+ rules' },
          { id: 'json', x: 80, y: 50, label: 'parameters.json', sublabel: 'WHP catalog' },

          // Bottom Layer - Output & Results (y: 80)
          { id: 'errors', x: 35, y: 80, label: 'Error Consolidation', sublabel: 'Grouping & line ranges' },
          { id: 'dialog', x: 65, y: 80, label: 'Results Dialog', sublabel: 'Tabbed by category' }
        ],
        connections: [
          // Top layer horizontal flow
          { from: 'upload', to: 'detect' },
          { from: 'detect', to: 'structure' },
          { from: 'structure', to: 'csv' },
          { from: 'csv', to: 'extract' },

          // Top to middle layer vertical connections
          { from: 'extract', to: 'params' },

          // Middle layer horizontal flow (reversed)
          { from: 'json', to: 'params' },
          { from: 'params', to: 'formats' },
          { from: 'formats', to: 'measure' },

          // Middle to bottom layer vertical connections
          { from: 'measure', to: 'errors' },

          // Bottom layer horizontal flow
          { from: 'errors', to: 'dialog' }
        ]
      }
    },
    {
      name: "ERDDAP",
      role: "Software Engineer · CCHDO / NOAA · Sep 2024 – Mar 2026",
      tagline: "Unified 40+ data formats into single API, enabling CORS-free access to 10,000+ datasets",
      tags: ["JavaScript", "jQuery", "HTML5"],
      icon: "🌊",
      visualBg: "#E8F4FD",
      liveUrl: "https://cchdo.github.io/erddap_search/",
      previewUrl: "https://cchdo.github.io/erddap_search/",
      allowIframe: true,
      details: [
        "Problem: Scientists struggled to access data across 40+ incompatible formats with CORS restrictions",
        "Solution: Built platform unifying formats into single API for CORS-free access",
        "Impact: Enables access to 10,000+ datasets, received GOOS Recognition 2025 for global impact"
      ],
      metrics: [
        { value: 10000, suffix: "+", label: "Datasets", hasCount: true },
        { value: 40, suffix: "+", label: "Formats Unified", hasCount: true },
        { value: "GOOS 2025", label: "Recognition" }
      ],
      showFlow: true,
      /* flowData: {
        nodes: [
          // Top Layer - Client Browser (evenly distributed)
          { id: 'html', x: 10, y: 20, label: 'index.html', sublabel: 'Static Page' },
          { id: 'js', x: 28, y: 20, label: 'main.js', sublabel: 'Vanilla JS' },
          { id: 'jquery', x: 46, y: 20, label: 'jQuery', sublabel: '3.5.1' },
          { id: 'bootstrap', x: 64, y: 20, label: 'Bootstrap', sublabel: '3.3.2' },
          { id: 'datatables', x: 75, y: 20, label: 'DataTables', sublabel: 'Results' },

          // Middle Layer - Browser APIs (aligned)
          { id: 'jsonp', x: 19, y: 50, label: 'JSONP', sublabel: 'CORS Bypass' },
          { id: 'ajax', x: 37, y: 50, label: 'AJAX', sublabel: 'Direct Calls' },
          { id: 'validate', x: 55, y: 50, label: 'Validation', sublabel: 'Client-side' },
          { id: 'cache', x: 73, y: 50, label: 'Cache', sublabel: 'Browser' },

          // Bottom Layer - ERDDAP (evenly spaced)
          { id: 'erddap', x: 20, y: 80, label: 'ERDDAP API', sublabel: 'data.pmel.noaa.gov' },
          { id: 'response', x: 45, y: 80, label: 'JSON/JSONP', sublabel: 'Response' },
          { id: 'formats', x: 90, y: 80, label: '40+ Formats', sublabel: 'Direct Download' }
        ],
        connections: [
          // Browser layer connections
          { from: 'html', to: 'js' },
          { from: 'js', to: 'jquery' },
          { from: 'jquery', to: 'bootstrap' },
          { from: 'bootstrap', to: 'datatables' },

          // Browser to API layer
          { from: 'js', to: 'jsonp' },
          { from: 'jquery', to: 'ajax' },
          { from: 'bootstrap', to: 'validate' },
          { from: 'datatables', to: 'cache' },

          // API layer connections
          { from: 'jsonp', to: 'ajax' },
          { from: 'ajax', to: 'validate' },
          { from: 'validate', to: 'cache' },

          // Direct to ERDDAP
          { from: 'jsonp', to: 'erddap' },
          { from: 'ajax', to: 'response' },

          // ERDDAP layer
          { from: 'erddap', to: 'response' },

          // DataTables to final formats output
          { from: 'datatables', to: 'formats' },
          { from: 'cache', to: 'formats' },
          { from: 'response', to: 'formats' }
        ]
      } */
    }
  ];

  useEffect(() => {
    // Use only featured projects for minimalist design
    setProjects(featuredProjects);
    setFilteredProjects(featuredProjects);
  }, []);


  useEffect(() => {
    const handleScroll = () => {
      const pageYOffset = window.pageYOffset;
      
      let newActiveSection = sections.current[0].id;

      sections.current.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop - 100;
          if (pageYOffset >= offsetTop) {
            newActiveSection = section.id;
          }
        }
      });

      setActiveSection(newActiveSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
    }
    setMobileNavOpen(false);
  };



  // Add CSS animation for modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);



  return (
    <PortfolioContainer>
      {/* Navigation */}
      <Navigation>
        <NavList>
          {sections.current.map((section) => (
            <NavItem key={section.id}>
              <NavLink
                active={activeSection === section.id}
                onClick={() => scrollToSection(section.id)}
                whileHover={{ opacity: 0.8 }}
                whileTap={{ opacity: 0.6 }}
              >
                {section.label}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
        
        <MobileNavButton
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          whileTap={{ scale: 0.95 }}
        >
          {mobileNavOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </MobileNavButton>
      </Navigation>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileNavOpen && (
          <MobileNav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {sections.current.map((section) => (
              <NavLink
                key={section.id}
                active={activeSection === section.id}
                onClick={() => scrollToSection(section.id)}
                whileHover={{ opacity: 0.8 }}
                whileTap={{ opacity: 0.6 }}
              >
                {section.label}
              </NavLink>
            ))}
          </MobileNav>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <HeroSection id="hero">
        <HeroBackground />
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
        <HeroContent>
          <LeftContent>
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <Name>Alden Bernstein</Name>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
            >
              <BioText>
                Hi, I'm Alden, a CS and ML senior at UC San Diego.
              </BioText>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65, ease: "easeOut" }}
            >
              <BioText>
                At <strong>Microsoft</strong>, I built two data tools for Xbox's data platform team, including an AI agent engineers can question in plain English.
              </BioText>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
            >
              <BioText>
                At CCHDO, a federal agency under NOAA, I built tools that help researchers access and validate climate data, including a validation tool with 50,000+ visits each month.
              </BioText>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.95, ease: "easeOut" }}
            >
              <BioText>
                During my last quarter at UCSD, I'm back with the CCHDO team, researching and building AI tools for <strong>NOAA</strong> climate data. Outside of that, I work on developing music software and indie games.
              </BioText>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1, ease: "easeOut" }}
            >
              <BioText style={{ marginTop: '1.5rem', opacity: 0.7 }}>
                NOAA AI Research (09/2026–12/2026)<br/>
                Microsoft Data Engineering Intern (06/2026–09/2026)<br/>
                UCSD Geological Data Center (03/2026–06/2026)<br/>
                CCHDO, NOAA Federal Data Office (09/2024–03/2026)
              </BioText>
            </motion.div>
          </LeftContent>
          
          <div>
            <AvatarContainer
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 1.3,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <AnimalCrossingAvatar />
            </AvatarContainer>
            <InteractiveHint
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: 0.6,
                y: 0
              }}
              transition={{
                duration: 0.6,
                delay: 2,
                ease: "easeOut"
              }}
            >
              <span>↻</span> Spin me around <span>↺</span>
            </InteractiveHint>
          </div>
        </HeroContent>
        
        <ScrollButton
          onClick={() => scrollToSection('repositories')}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            delay: 1.4, 
            duration: 0.6,
            ease: "easeOut"
          }}
          whileHover={{ scale: 1.1 }}
        >
          <motion.div
            style={{
              width: '24px',
              height: '36px',
              border: '2px solid currentColor',
              borderRadius: '12px',
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '6px'
            }}
          >
            <motion.div
              style={{
                width: '3px',
                height: '8px',
                backgroundColor: 'currentColor',
                borderRadius: '2px'
              }}
              animate={{ 
                y: [0, 10, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
          <motion.div
            style={{
              display: 'flex',
              gap: '4px',
              marginTop: '8px'
            }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
          </motion.div>
        </ScrollButton>
        </motion.div>
      </HeroSection>


      {/* Projects Section - Vertical Case Studies */}
      <RepositorySection id="repositories">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <SectionTitle>
            {["Where", "I've", "Been", "Building"].map((word, i) => (
              <span key={word}>
                {i > 0 && ' '}
                <motion.span
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.12, ease: "easeOut" }}
                  style={{ display: 'inline-block' }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </SectionTitle>

          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            {featuredProjects.map((project) => (
              <MinimalCaseStudy 
                key={project.name} 
                project={project}
              />
            ))}
          </div>
        </motion.div>
      </RepositorySection>

      {/* Contact Section */}
      <Contact />

    </PortfolioContainer>
  );
};

export default Portfolio;